import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { HOME_PATH } from "../../config/products";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import {
  cancelAccountDeletion,
  clearDeletionState,
  fetchDeletionEligibility,
  requestAccountDeletion,
} from "../../redux/accountSlice";
import { logoutAppUser } from "../../redux/appAuthSlice";
import { formatAppDateTime } from "../../utils/appTimeFormat";

const formatScheduleDate = (value) => {
  if (!value) return "";
  try {
    return formatAppDateTime(value);
  } catch {
    return String(value);
  }
};

const CloseAccountModal = ({ open, onClose, onNotify }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const titleId = useId();

  const eligibility = useSelector((s) => s.account.deletionEligibility);
  const eligibilityStatus = useSelector((s) => s.account.deletionEligibilityStatus);
  const actionStatus = useSelector((s) => s.account.deletionActionStatus);
  const deletionError = useSelector((s) => s.account.deletionError);

  const [password, setPassword] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  const [queueRequestSent, setQueueRequestSent] = useState(false);
  const [localError, setLocalError] = useState("");

  const loadingEligibility = eligibilityStatus === "loading";
  const busy = actionStatus === "loading";
  const schedule = eligibility?.schedule || {};
  const deletionQueue = eligibility?.queue || schedule.queue || {};
  const checks = eligibility?.checks || [];
  const queuedAt =
    schedule.queuedAt ||
    schedule.queuedDeletionAt ||
    schedule.accountDeletionQueuedAt ||
    eligibility?.queuedAt ||
    eligibility?.accountDeletionQueuedAt ||
    deletionQueue.queuedAt;
  const isDeletionQueued = Boolean(
    schedule.queued ||
      schedule.queuedForDeletion ||
      eligibility?.queued ||
      eligibility?.queuedForDeletion ||
      queuedAt
  );
  const scheduledAt = schedule.scheduledDeletionAt;
  const awaitingEmail =
    schedule.awaitingEmailConfirmation && !scheduledAt && !isDeletionQueued;
  const canRequestDeletion =
    eligibility?.allowed &&
    !loadingEligibility &&
    !scheduledAt &&
    !isDeletionQueued;
  const canJoinDeletionQueue =
    eligibility &&
    !eligibility.allowed &&
    !loadingEligibility &&
    !scheduledAt &&
    !isDeletionQueued;

  useEffect(() => {
    if (!open) return undefined;

    setPassword("");
    setRequestSent(false);
    setQueueRequestSent(false);
    setLocalError("");
    dispatch(clearDeletionState());
    dispatch(fetchDeletionEligibility());

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, dispatch, onClose]);

  if (!open) return null;

  const errorText = localError || deletionError || "";
  const passwordReady = password.trim().length > 0;

  const refreshEligibility = async () => {
    setLocalError("");
    const result = await dispatch(fetchDeletionEligibility());
    if (fetchDeletionEligibility.rejected.match(result)) {
      setLocalError(String(result.payload || "Could not refresh status."));
    }
  };

  const submitRequest = async () => {
    if (!eligibility?.allowed) return;

    setLocalError("");
    setRequestSent(false);
    setQueueRequestSent(false);

    const result = await dispatch(
      requestAccountDeletion({ password: password.trim(), queueIfBlocked: false })
    );

    if (requestAccountDeletion.fulfilled.match(result)) {
      setRequestSent(true);
      setPassword("");
      onNotify?.(
        "success",
        "Account deleted",
        result.payload?.message || "Your account has been deleted."
      );
      dispatch(logoutAppUser());
      onClose?.();
      navigate(HOME_PATH);
      return;
    }

    const message =
      typeof result.payload === "string"
        ? result.payload
        : result.payload?.message || "Could not delete your account.";
    setLocalError(message);
  };

  const submitQueueDeletion = async () => {
    if (!eligibility || eligibility.allowed) return;

    setLocalError("");
    setRequestSent(false);
    setQueueRequestSent(false);

    const result = await dispatch(
      requestAccountDeletion({ password: password.trim(), queueIfBlocked: true })
    );

    if (requestAccountDeletion.fulfilled.match(result)) {
      setQueueRequestSent(true);
      setPassword("");
      onNotify?.(
        "success",
        "Deletion queue active",
        result.payload?.message ||
          "Your account is in the deletion queue. We will keep checking and delete it once blocking activity is settled."
      );
      await dispatch(fetchDeletionEligibility());
      return;
    }

    const message =
      typeof result.payload === "string"
        ? result.payload
        : result.payload?.message || "Could not join the deletion queue.";
    setLocalError(message);
  };

  const handleCancelClosure = async () => {
    setLocalError("");
    setRequestSent(false);
    setQueueRequestSent(false);

    const result = await dispatch(cancelAccountDeletion());

    if (cancelAccountDeletion.fulfilled.match(result)) {
      onNotify?.(
        "success",
        isDeletionQueued ? "Queue cancelled" : "Deletion cancelled",
        result.payload?.message || "Your account will stay active."
      );
      await dispatch(fetchDeletionEligibility());
      return;
    }

    setLocalError(String(result.payload || "Could not cancel deletion."));
  };

  return createPortal(
    <div className="prof-close-root" role="presentation">
      <button
        type="button"
        className="prof-close-backdrop"
        aria-label="Close dialog"
        disabled={busy}
        onClick={() => {
          if (!busy) onClose?.();
        }}
      />

      <div
        className="prof-close-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="prof-close-head">
          <span className="prof-close-icon" aria-hidden>
            <Trash2 size={18} />
          </span>
          <div className="prof-close-head-copy">
            <p className="prof-close-eyebrow">Danger zone</p>
            <h2 id={titleId}>Delete your account</h2>
          </div>
          <button
            type="button"
            className="prof-close-x"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            <X size={14} />
          </button>
        </header>

        <p className="prof-close-sub">
          First, we check whether anything is still open on your account. You can
          delete now if every check passes, or join the deletion queue if some
          activity is still pending.
        </p>

        {loadingEligibility ? (
          <div className="prof-close-loading">
            <Loader2 size={18} className="animate-spin" aria-hidden />
            <span>Checking your account…</span>
          </div>
        ) : (
          <>
            <div className="prof-close-check-intro">
              <strong>Deletion checks</strong>
              <span>Red items must finish before final deletion can complete.</span>
            </div>
            <div className="prof-close-checks">
              {checks.map((check) => (
                <div
                  key={check.id}
                  className={`prof-close-check${check.ok ? "" : " prof-close-check-bad"}`}
                >
                  {check.ok ? (
                    <CheckCircle2 size={14} aria-hidden />
                  ) : (
                    <AlertCircle size={14} aria-hidden />
                  )}
                  <div>
                    <strong>{check.title}</strong>
                    {!check.ok && check.detail ? <span>{check.detail}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {errorText ? <p className="prof-close-error">{errorText}</p> : null}

        {scheduledAt ? (
          <div className="prof-close-schedule">
            <strong>Deletion scheduled</strong>
            <span>{formatScheduleDate(scheduledAt)}</span>
            <p>
              After this date your account will be removed if nothing blocks deletion.
              You can cancel anytime before then.
            </p>
            <button
              type="button"
              className="prof-close-btn prof-close-btn-ghost"
              onClick={handleCancelClosure}
              disabled={busy}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : null}
              Cancel deletion
            </button>
          </div>
        ) : null}

        {isDeletionQueued && !scheduledAt ? (
          <div className="prof-close-queue">
            <strong>Deletion queue active</strong>
            {queuedAt ? <span>Queued {formatScheduleDate(queuedAt)}</span> : null}
            <p>
              We&apos;ll keep checking your blocking activity. When everything is
              settled, your account will move into deletion automatically.
            </p>
            <p className="prof-close-queue-warning">
              While queued, you cannot start or join new activity that would block
              deletion unless you cancel the request first.
            </p>
            <button
              type="button"
              className="prof-close-btn prof-close-btn-ghost"
              onClick={handleCancelClosure}
              disabled={busy}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : null}
              Cancel deletion queue
            </button>
          </div>
        ) : null}

        {awaitingEmail ? (
          <div className="prof-close-schedule">
            <strong>Legacy confirmation pending</strong>
            <p>
              This account has an older email-confirmation request pending. You can
              refresh status or cancel deletion.
            </p>
            <button
              type="button"
              className="prof-close-btn prof-close-btn-outline"
              onClick={refreshEligibility}
              disabled={loadingEligibility || busy}
            >
              <RefreshCw size={12} aria-hidden />
              Refresh status
            </button>
          </div>
        ) : null}

        {requestSent ? (
          <p className="prof-close-sent">Account deleted. Signing you out…</p>
        ) : null}

        {queueRequestSent ? (
          <p className="prof-close-sent">
            Deletion queue request saved. We will continue checking your account.
          </p>
        ) : null}

        {canRequestDeletion ? (
          <div className="prof-close-form">
            <label className="prof-close-label" htmlFor="prof-close-password">
              Confirm password
            </label>
            <input
              id="prof-close-password"
              type="password"
              className="prof-close-input"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Your password"
              disabled={busy}
              autoComplete="current-password"
            />
            <button
              type="button"
              className={`prof-close-btn prof-close-btn-danger${
                !passwordReady ? " prof-close-btn-disabled" : ""
              }`}
              onClick={submitRequest}
              disabled={busy || !passwordReady}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : null}
              Delete account now
            </button>
          </div>
        ) : null}

        {canJoinDeletionQueue ? (
          <div className="prof-close-prompt">
            <div className="prof-close-prompt-head">
              <Clock size={14} aria-hidden />
              <strong>Pending activity found</strong>
            </div>
            <p>
              You cannot delete immediately because some items are still open. Join
              the deletion queue and we will delete the account automatically when
              those items are finished.
            </p>
            <ul className="prof-close-rules">
              <li>We keep checking your account.</li>
              <li>You cannot start or join new blocking activity.</li>
              <li>You can cancel the queue anytime before deletion.</li>
            </ul>
            <label className="prof-close-label" htmlFor="prof-close-queue-password">
              Confirm password
            </label>
            <input
              id="prof-close-queue-password"
              type="password"
              className="prof-close-input"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Your password"
              disabled={busy}
              autoComplete="current-password"
            />
            <button
              type="button"
              className={`prof-close-btn prof-close-btn-danger${
                !passwordReady ? " prof-close-btn-disabled" : ""
              }`}
              onClick={submitQueueDeletion}
              disabled={busy || !passwordReady}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : null}
              Confirm and join queue
            </button>
          </div>
        ) : null}

        <button
          type="button"
          className="prof-close-btn prof-close-btn-ghost prof-close-dismiss"
          onClick={onClose}
          disabled={busy}
        >
          {loadingEligibility ? "Please wait…" : "Close"}
        </button>

        {!loadingEligibility && eligibility && !eligibility.allowed && !isDeletionQueued ? (
          <button
            type="button"
            className="prof-close-retry"
            onClick={refreshEligibility}
            disabled={busy}
          >
            <RefreshCw size={11} aria-hidden />
            Check again
          </button>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default CloseAccountModal;
